import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  HiOutlineUserGroup,
  HiOutlineBriefcase,
  HiOutlineAcademicCap,
  HiOutlineSparkles
} from "react-icons/hi";
// import Header from "../Header/Header";
import Cookies from "js-cookie";

const Committee = () => {
  const [committeeData, setCommitteeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedMember, setSelectedMember] = useState(null);

  useEffect(() => {
    const fetchCommitteeData = async () => {
      try {
        const token = Cookies.get("userToken");
        if (!token) throw new Error("No authentication token found");

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/apis/admin/getAllExecutiveCommittee`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            }
          }
        );

        if (!response.ok) throw new Error("Failed to fetch committee data");

        const data = await response.json();
        setCommitteeData(Array.isArray(data?.data) ? data.data : []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCommitteeData();
  }, []);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  const heroVariants = {
    hidden: { opacity: 0, scale: 1.2 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 1.2, ease: "easeOut" }
    }
  };

  const cardVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    },
    hover: {
      y: -10,
      scale: 1.02,
      transition: { duration: 0.3 }
    }
  };

  const MemberCard = ({ member, loading, index, isChairman = false }) => (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      whileHover="hover"
      className="relative group"
    >
      <div className={`relative flex flex-col items-center py-8 ${isChairman ? 'scale-110 md:scale-125' : ''}`}>
        {/* Animated background glow */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-gray-300 to-gray-400 rounded-3xl opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-300"
          animate={isChairman ? {
            scale: [1, 1.1, 1],
            opacity: [0.2, 0.3, 0.2]
          } : {}}
          transition={isChairman ? {
            duration: 3,
            repeat: Infinity,
            repeatType: "reverse"
          } : {}}
        />

        {/* Image Container */}
        <motion.div
          className={`relative w-64 h-64 sm:w-72 sm:h-72 ${isChairman ? 'md:w-80 md:h-80' : ''} rounded-2xl overflow-hidden shadow-xl group-hover:shadow-2xl transition-shadow duration-300 border-4 border-white ${
            isChairman
              ? 'bg-gradient-to-br from-gray-300 to-gray-400'
              : 'bg-gradient-to-br from-gray-100 to-gray-200'
          }`}
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.3 }}
        >
          {loading ? (
            <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 animate-pulse" />
          ) : (
            <>
              <motion.img
                src={member.image || `https://ui-avatars.com/api/?name=${member.name || 'Member'}&background=6b7280&color=fff&size=200`}
                alt={member.name || 'Committee Member'}
                className="w-full h-full object-cover"
                whileHover={{ scale: 1.1 }}
                transition={{ duration: 0.5 }}
              />
              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </>
          )}
        </motion.div>

        {/* Chairman Star/Crown */}
        {isChairman && !loading && (
          <motion.div
            className="absolute -top-4 left-1/2 transform -translate-x-1/2"
            animate={{
              y: [0, -5, 0],
              rotate: [0, 5, -5, 0]
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          >
            <div className="bg-gradient-to-r from-gray-600 to-gray-700 p-2 rounded-full shadow-lg">
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            </div>
          </motion.div>
        )}

        {/* Info Card - Now smaller and positioned differently */}
        <motion.div
          className={`absolute -bottom-4 w-48 sm:w-56 bg-white rounded-lg shadow-lg px-3 sm:px-4 py-2 text-center border border-gray-200`}
          whileHover={{ y: -3 }}
          transition={{ duration: 0.2 }}
        >
          {loading ? (
            <>
              <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto mb-1 animate-pulse" />
              <div className="h-3 bg-gray-200 rounded w-1/2 mx-auto animate-pulse" />
            </>
          ) : (
            <>
              <motion.h3
                className="text-sm sm:text-base font-bold text-gray-800 truncate"
                whileHover={{ scale: 1.05 }}
              >
                {member.name || 'Member Name'}
              </motion.h3>
              <p className="text-xs text-gray-500 font-medium truncate">
                {member.designation || (isChairman ? 'Chairman' : 'Committee Member')}
              </p>
            </>
          )}
        </motion.div>
      </div>
    </motion.div>
  );

  const SkeletonLoader = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 px-4">
      {[1, 2, 3].map((i) => (
        <MemberCard key={i} member={{}} loading={true} index={i} />
      ))}
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="w-full min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 relative overflow-hidden"
    >
      {/* Animated Background Elements - Light Gray */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full mix-blend-multiply filter blur-3xl opacity-40"
          animate={{
            x: [0, 50, 0],
            y: [0, -30, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />
        <motion.div
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full mix-blend-multiply filter blur-3xl opacity-40"
          animate={{
            x: [0, -50, 0],
            y: [0, 30, 0],
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />

        {/* Floating Icons - Light Gray */}
        <motion.div
          className="absolute top-1/4 left-1/4 text-gray-200/30 hidden lg:block"
          animate={{
            y: [0, -20, 0],
            rotate: [0, 10, -10, 0]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        >
          <HiOutlineUserGroup size={60} />
        </motion.div>

        <motion.div
          className="absolute bottom-1/4 right-1/4 text-gray-200/30 hidden lg:block"
          animate={{
            y: [0, 20, 0],
            rotate: [0, -10, 10, 0]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        >
          <HiOutlineBriefcase size={70} />
        </motion.div>
      </div>

      {/* Hero Section - Light Overlay */}
      <div className="relative w-full h-[50vh] sm:h-[55vh] md:h-[60vh] lg:h-[70vh] overflow-hidden">
        <motion.img
          variants={heroVariants}
          initial="hidden"
          animate="visible"
          src="assets/Committee.png"
          alt="Committee Background"
          className="w-full h-full object-cover object-center"
        />

        {/* Light Gradient Overlay - Gray */}
        <div className="absolute inset-0 bg-gradient-to-t from-gray-200/70 via-gray-100/50 to-transparent" />

        {/* Floating Elements - Light */}
        <motion.div
          className="absolute top-20 left-10 text-gray-300/40 hidden lg:block"
          animate={{
            y: [0, -20, 0],
            rotate: [0, 10, -10, 0]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        >
          <HiOutlineSparkles size={60} />
        </motion.div>

        <motion.div
          className="absolute bottom-20 right-10 text-gray-300/40 hidden lg:block"
          animate={{
            y: [0, 20, 0],
            rotate: [0, -10, 10, 0]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        >
          <HiOutlineAcademicCap size={50} />
        </motion.div>

        {/* Hero Content - Dark text for contrast */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 sm:px-6">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 sm:mb-4 text-gray-800"
          >
            Executive Committee
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-sm sm:text-base md:text-lg lg:text-xl font-medium text-gray-700 max-w-2xl px-4 leading-relaxed"
          >
            The Executive Committee leads with vision, overseeing strategic decisions,
            fostering collaboration, and driving the organization towards its goals.
          </motion.p>

          {/* Stats Counter - Light Gray */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex items-center space-x-4 mt-6 sm:mt-8 bg-white/40 backdrop-blur-sm rounded-full px-4 sm:px-6 py-2 sm:py-3 border border-gray-200/60 shadow-sm"
          >
            <div className="flex items-center space-x-2">
              <HiOutlineUserGroup className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
              <span className="text-gray-600 text-xs sm:text-sm font-medium">
                {committeeData.length} Members
              </span>
            </div>
            <div className="w-px h-4 bg-gray-200" />
            <div className="flex items-center space-x-2">
              <HiOutlineBriefcase className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
              <span className="text-gray-600 text-xs sm:text-sm font-medium">
                Leadership Team
              </span>
            </div>
          </motion.div>

          {/* Scroll Indicator - Light Gray */}
          <motion.div
            className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <div className="w-6 h-10 border-2 border-gray-300 rounded-full flex justify-center">
              <div className="w-1 h-2 bg-gray-300 rounded-full mt-2" />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-7xl mx-auto px-4 py-12 sm:py-16 md:py-20"
      >
        {/* Chairman Section */}
        <motion.div variants={itemVariants} className="mb-24 sm:mb-32 text-center">
          <motion.h3
            className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-700 mb-8 sm:mb-12 flex items-center justify-center space-x-2"
            animate={{
              scale: [1, 1.05, 1],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          >
            <svg className="w-6 h-6 sm:w-8 sm:h-8 text-gray-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            <span>Chairman</span>
            <svg className="w-6 h-6 sm:w-8 sm:h-8 text-gray-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </motion.h3>

          <div className="flex justify-center">
            {committeeData[0] ? (
              <MemberCard member={committeeData[0]} loading={loading} index={0} isChairman={true} />
            ) : (
              <MemberCard member={{}} loading={true} index={0} isChairman={true} />
            )}
          </div>
        </motion.div>

        {/* Committee Members */}
        <motion.div variants={itemVariants}>
          <motion.h3
            className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-700 mb-8 sm:mb-12 text-center"
          >
            Committee Members
          </motion.h3>

          {loading ? (
            <SkeletonLoader />
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-12 px-4">
                {(committeeData.slice(1, 4)).map((member, index) => (
                  <MemberCard
                    key={member._id || index}
                    member={member}
                    loading={false}
                    index={index + 1}
                  />
                ))}
              </div>

              {/* Additional Members */}
              {committeeData.length > 4 && (
                <motion.div
                  variants={itemVariants}
                  className="mt-16 sm:mt-20"
                >
                  <motion.h4
                    className="text-lg sm:text-xl font-semibold text-gray-600 mb-8 text-center"
                  >
                    Additional Members
                  </motion.h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-12 px-4">
                    {committeeData.slice(4).map((member, index) => (
                      <MemberCard
                        key={member._id}
                        member={member}
                        loading={false}
                        index={index + 4}
                      />
                    ))}
                  </div>
                </motion.div>
              )}
            </>
          )}
        </motion.div>

        {/* Error State */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center p-6 sm:p-8 bg-red-50 border-2 border-red-200 rounded-2xl max-w-md mx-auto mt-8"
            >
              <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <p className="text-red-600 text-base sm:text-lg font-semibold">Error: {error}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Empty State */}
        <AnimatePresence>
          {!loading && !error && committeeData.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center p-8 sm:p-12 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg max-w-md mx-auto mt-8 border border-gray-200"
            >
              <div className="w-20 h-20 mx-auto bg-gradient-to-r from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-4">
                <HiOutlineUserGroup className="w-10 h-10 text-gray-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-700 mb-2">No Committee Members</h3>
              <p className="text-gray-600">Check back later for updates</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Member Detail Modal */}
        <AnimatePresence>
          {selectedMember && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
              onClick={() => setSelectedMember(null)}
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl border border-gray-200"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center">
                    <span className="text-2xl text-gray-600 font-bold">
                      {selectedMember.name?.charAt(0) || 'M'}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">{selectedMember.name || 'Member Name'}</h3>
                    <p className="text-gray-600 font-medium">{selectedMember.designation || 'Committee Member'}</p>
                  </div>
                </div>
                <div className="space-y-3 bg-gray-50 rounded-xl p-4">
                  <p className="text-gray-600"><span className="font-semibold text-gray-700">Email:</span> {selectedMember.email || 'Not available'}</p>
                  <p className="text-gray-600"><span className="font-semibold text-gray-700">Phone:</span> {selectedMember.phone || 'Not available'}</p>
                  <p className="text-gray-600"><span className="font-semibold text-gray-700">Bio:</span> {selectedMember.bio || 'No bio available'}</p>
                </div>
                <button
                  onClick={() => setSelectedMember(null)}
                  className="mt-6 px-4 py-2 bg-gray-700 text-white rounded-lg w-full hover:bg-gray-800 transition"
                >
                  Close
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

export default Committee;